import React from 'react';
import { View, Text, TouchableOpacity, ScrollView, StyleSheet } from 'react-native';
import { Feather } from '@expo/vector-icons';
import { Paprika, PaprikaShadows } from '../../styles/theme';
import { useTranslation } from 'react-i18next';

export interface RailStep {
  globalNumber: number;
  sectionName: string | null;
  /** Fallback one-line preview when the recipe has no section names. */
  preview: string;
}

interface Props {
  steps: RailStep[];
  currentIndex: number;
  onGoToStep: (index: number) => void;
}

/**
 * Desktop right rail (handoff `StepRail`): warm wash panel listing every step.
 * Done = sage check · current = terracotta on a floating white card · upcoming
 * = muted. All rows stay tappable — no locking.
 */
export const StepRail: React.FC<Props> = ({ steps, currentIndex, onGoToStep }) => {
  const { t } = useTranslation();
  return (
    <View style={styles.rail}>
      <Text style={styles.railTitle}>{t('cooking.theSteps')}</Text>
      <ScrollView showsVerticalScrollIndicator={false}>
        {steps.map((step, i) => {
          const done = i < currentIndex;
          const active = i === currentIndex;
          return (
            <TouchableOpacity
              key={i}
              style={[styles.row, active && styles.rowActive]}
              onPress={() => onGoToStep(i)}
            >
              <View
                style={[
                  styles.badge,
                  done && styles.badgeDone,
                  active && styles.badgeActive,
                ]}
              >
                {done ? (
                  <Feather name="check" size={13} color={Paprika.onPrimary} />
                ) : (
                  <Text style={[styles.badgeNum, active && styles.badgeNumActive]}>
                    {step.globalNumber}
                  </Text>
                )}
              </View>
              <View style={styles.rowText}>
                <Text style={[styles.stepMicro, active && styles.stepMicroActive]}>
                  {t('cooking.stepNumber', { number: step.globalNumber })}
                </Text>
                <Text
                  style={[styles.stepName, active && styles.stepNameActive]}
                  numberOfLines={1}
                >
                  {step.sectionName ?? step.preview}
                </Text>
              </View>
            </TouchableOpacity>
          );
        })}
      </ScrollView>
    </View>
  );
};

const styles = StyleSheet.create({
  rail: {
    width: 288,
    backgroundColor: Paprika.bgWash,
    borderLeftWidth: 1,
    borderLeftColor: Paprika.hairline,
    paddingTop: 26,
    paddingHorizontal: 18,
  },
  railTitle: {
    fontSize: 10,
    fontFamily: 'Inter-SemiBold',
    fontWeight: '600',
    color: Paprika.textMuted,
    letterSpacing: 1.2,
    textTransform: 'uppercase',
    paddingHorizontal: 6,
    paddingBottom: 12,
  },
  row: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 13,
    paddingVertical: 12,
    paddingHorizontal: 14,
    borderRadius: 13,
    marginBottom: 3,
    borderWidth: 1,
    borderColor: 'transparent',
  },
  rowActive: {
    backgroundColor: Paprika.cardBg,
    borderColor: Paprika.hairline,
    ...PaprikaShadows.card,
  },
  badge: {
    width: 26,
    height: 26,
    borderRadius: 13,
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
    backgroundColor: Paprika.surfaceLow,
  },
  badgeDone: {
    backgroundColor: Paprika.success,
  },
  badgeActive: {
    backgroundColor: Paprika.accent,
  },
  badgeNum: {
    fontSize: 12,
    fontFamily: 'Inter-SemiBold',
    fontWeight: '600',
    color: Paprika.textMuted,
  },
  badgeNumActive: {
    color: Paprika.onPrimary,
  },
  rowText: {
    flex: 1,
    minWidth: 0,
  },
  stepMicro: {
    fontSize: 10,
    fontFamily: 'Inter-SemiBold',
    fontWeight: '600',
    letterSpacing: 1,
    textTransform: 'uppercase',
    color: Paprika.textMuted,
  },
  stepMicroActive: {
    color: Paprika.accent,
  },
  stepName: {
    fontSize: 13.5,
    fontFamily: 'Inter-Medium',
    fontWeight: '500',
    color: Paprika.textSecondary,
    marginTop: 1,
  },
  stepNameActive: {
    fontFamily: 'Inter-SemiBold',
    fontWeight: '600',
    color: Paprika.textPrimary,
  },
});
